// ============================================================================
// RouteDemandSystem.js - Route Demand Visual System
// ============================================================================
// Tracks demand saturation per route. Flying the same route repeatedly
// depletes demand, reducing yield. Demand recovers daily.
// Route colors change from green (high demand) to red (low demand).
// ============================================================================

/**
 * Get a canonical route key (alphabetically sorted so PDX-AUS === AUS-PDX)
 * @param {string} origin - Origin airport ICAO code
 * @param {string} destination - Destination airport ICAO code
 * @returns {string} Canonical route key like "AUS-PDX"
 */
export function getRouteKey(origin, destination) {
  return [origin, destination].sort().join("-");
}

/**
 * Deplete demand on a route when a flight completes
 * @param {string} origin - Origin airport ICAO code
 * @param {string} destination - Destination airport ICAO code
 * @param {Object} demandState - The routeDemand state object
 * @returns {number} The new demand value for this route
 */
export function depleteRoute(origin, destination, demandState) {
  const key = getRouteKey(origin, destination);

  // Initialize to 100 if first flight on this route
  if (demandState[key] === undefined) {
    demandState[key] = 100;
  }

  // Deplete by 1.5%, floor at 30%
  demandState[key] = Math.max(30, demandState[key] - 1.5);

  return demandState[key];
}

/**
 * Recover all routes' demand at day advance
 * Called once per day transition
 * @param {Object} demandState - The routeDemand state object (mutated in place)
 * @param {Object} abandonedRoutes - Routes with cooldown periods (optional)
 * @param {number} currentDay - Current game day (optional)
 */
export function recoverAllRoutes(demandState, abandonedRoutes = {}, currentDay = 0) {
  for (const key of Object.keys(demandState)) {
    // Check if this route is abandoned and in cooldown
    const abandoned = abandonedRoutes[key];
    if (abandoned && currentDay < abandoned.cooldownUntilDay) {
      // Route is in cooldown - NO recovery, stays at abandoned demand level
      // This prevents players from abandoning stale routes and immediately getting 100% demand back
      continue;
    }

    // Normal recovery: 3% per day, cap at 100%
    demandState[key] = Math.min(100, demandState[key] + 3);
  }

  // Clean up expired cooldowns
  for (const key of Object.keys(abandonedRoutes)) {
    if (currentDay >= abandonedRoutes[key].cooldownUntilDay) {
      delete abandonedRoutes[key];
    }
  }
}

/**
 * Get yield multiplier based on route demand
 * @param {string} origin - Origin airport ICAO code
 * @param {string} destination - Destination airport ICAO code
 * @param {Object} demandState - The routeDemand state object
 * @returns {number} Multiplier from 0.3 (30% demand) to 1.0 (100% demand)
 */
export function getYieldMultiplier(origin, destination, demandState) {
  const key = getRouteKey(origin, destination);
  const demand = demandState[key] ?? 100;

  // demand 100 = 1.0x, demand 30 = 0.3x
  return demand / 100;
}

/**
 * Get route line color based on demand saturation
 * Uses HSL color space for smooth gradient from green (full) to red (depleted)
 *
 * NON-NEGOTIABLE: This returns a continuous HSL color.
 * DO NOT round the hue. DO NOT use color bands. DO NOT simplify.
 * Every decimal point of demand = a different color.
 *
 * @param {string} origin - Origin airport ICAO code
 * @param {string} destination - Destination airport ICAO code
 * @param {Object} demandState - The routeDemand state object
 * @returns {string} HSL color string like "hsl(60, 80%, 45%)"
 */
export function getRouteColor(origin, destination, demandState) {
  const key = getRouteKey(origin, destination);
  const demand = demandState[key] ?? 100;

  // Normalize demand 30-100 to 0-1
  // 1 = full demand (green), 0 = floor (red)
  const normalized = Math.max(0, Math.min(1, (demand - 30) / 70));

  // HSL hue: 0 = red, 120 = green
  // This gives you every color in between:
  // red -> orange -> yellow -> lime -> green
  // Thousands of distinct values, not bands
  const hue = normalized * 120;

  // Saturation 80% keeps it vivid
  // Lightness 45% keeps it readable on dark map
  return `hsl(${hue}, 80%, 45%)`;
}

/**
 * Create initial route demand state
 * @returns {Object} Empty demand state object
 */
export function createRouteDemandState() {
  return {};
}

/**
 * Get demand percentage for a route (for UI display)
 * @param {string} origin - Origin airport ICAO code
 * @param {string} destination - Destination airport ICAO code
 * @param {Object} demandState - The routeDemand state object
 * @returns {number} Demand as percentage 30-100
 */
export function getRouteDemand(origin, destination, demandState) {
  const key = getRouteKey(origin, destination);
  return demandState[key] ?? 100;
}
